/**
 * HoldDatePicker — the hold calendar.
 *
 * Ported from the Scale mediator portal's `MultiDatePicker`
 * (components/shared/FormControls.jsx). It lives here rather than in CM's
 * shared components because nothing else in Case Manager picks several dates
 * at once, and the cell painting below is specific to hold states.
 *
 * The per-date chooser popover the first Scale build had is deliberately NOT
 * here. It opened on a part-booked day so the remaining half could be taken,
 * which meant availability was only ever visible for the one day it was
 * anchored to. The booking slot is chosen in HoldComposer beside the calendar,
 * where the buttons can show which halves are free for the ACTIVE date.
 *
 * A day with NO free half is not selectable at all — offering a day that
 * cannot take morning, afternoon or full day only produces a selection the
 * save would reject.
 *
 * `value` is the selection array: [{ date, portion, preset, ... }]. This
 * component reads `date` and whether the entry is complete; the composer owns
 * the rest.
 */
import React, { useEffect, useState } from 'react';
import { FONT } from './holdTheme';

const pad = (n) => String(n).padStart(2, '0');
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/* Six weeks, Monday-first, so the grid never changes height between months. */
const monthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - lead);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return { date: d, inMonth: d.getMonth() === month };
  });
};

const SLOTS = ['morning', 'afternoon'];

export default function HoldDatePicker({
  value = [],
  activeIso = '',
  onPick,
  minIso,
  dayMeta = null,
  onMonthChange,
  loading = false,
}) {
  const today = new Date();
  const todayIso = toIso(today);
  const floor = minIso || todayIso;
  const selected = Array.isArray(value) ? value : [];
  const byDate = new Map(selected.map((v) => [v.date, v]));
  const firstSel = fromIso([...byDate.keys()].sort()[0]);
  const [vy, setVy] = useState((firstSel || today).getFullYear());
  const [vm, setVm] = useState((firstSel || today).getMonth());
  const grid = monthGrid(vy, vm);

  useEffect(() => {
    if (!onMonthChange || grid.length === 0) return;
    onMonthChange(toIso(grid[0].date), toIso(grid[grid.length - 1].date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vy, vm]);

  const prev = () => {
    const m = vm - 1;
    if (m < 0) { setVm(11); setVy(vy - 1); } else setVm(m);
  };
  const next = () => {
    const m = vm + 1;
    if (m > 11) { setVm(0); setVy(vy + 1); } else setVm(m);
  };

  /* 'unavailable' — the mediator has not opened that half — must be listed, or
     it falls through to 'none' and the day reports the wrong reason. Either
     way it is not holdable, so an unrecognised state still fails closed rather
     than offering a slot the save would refuse. */
  const slotState = (iso, slot) => {
    if (!dayMeta) return 'holdable';
    const d = dayMeta[iso];
    if (!d) return 'none';
    const st = d.slots?.[slot]?.state;
    return st === 'booked' || st === 'held' || st === 'holdable' || st === 'unavailable'
      ? st
      : 'none';
  };
  const freeSlots = (iso) => SLOTS.filter((s) => slotState(iso, s) === 'holdable');
  const isComplete = (sel) => !!(sel && sel.portion && sel.preset);

  const cell = (d) => {
    const iso = toIso(d);
    const outside = d.getMonth() !== vm;
    const past = iso < floor;
    const sel = byDate.get(iso);
    const free = freeSlots(iso);
    // Nothing free -> not selectable. Either fully booked, or wholly held.
    const blocked = !outside && !past && free.length === 0;
    const partial = !outside && !past && free.length === 1;
    const disabled = outside || past || blocked;

    /* A day whose two halves differ is PAINTED as two halves. One flat tone
       per day could only ever say "something about this day is not free"; it
       could not say WHICH half, so the answer lived in a tooltip you had to go
       looking for. Morning on top, afternoon below, with a HARD stop at 50% —
       a soft gradient reads as a rendering fault, a crisp line reads as
       deliberate. It also rescues held-morning + booked-afternoon, which under
       a flat tone looked exactly like an ordinary fully-booked day. */
    const HALF_TONE = {
      holdable: '#FFFFFF',
      held: '#FBF1DD',
      booked: '#F2F1EE',
      unavailable: '#F2F1EE',
      none: '#F2F1EE',
    };
    const mState = slotState(iso, 'morning');
    const aState = slotState(iso, 'afternoon');
    const splitHalves = !outside && !past && mState !== aState;
    const anyHeld = mState === 'held' || aState === 'held';
    // Both halves held: not pickable, but emphatically not "booked" either.
    const fullyHeld = !outside && !past && mState === 'held' && aState === 'held';

    let style = {
      position: 'relative', aspectRatio: '1 / 1', border: '1.5px solid transparent',
      borderRadius: 9, background: 'none', fontFamily: FONT, fontSize: 13.5,
      fontWeight: 500, color: '#1A1A17', cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      transition: 'background .13s, border-color .13s, color .13s, box-shadow .13s',
    };
    if (outside || past) style = { ...style, color: '#CFCEC7', cursor: 'default' };
    else if (splitHalves && !sel)
      style = {
        ...style,
        background: `linear-gradient(to bottom, ${HALF_TONE[mState]} 0 50%, ${HALF_TONE[aState]} 50% 100%)`,
        // Amber ring when a half is held so a held day still reads as amber at
        // a glance; a plain grey ring when the blocked half is merely booked.
        borderColor: anyHeld ? '#E3C489' : '#DEDDD7',
        // No strike-through even when neither half is pickable. The strike says
        // "nothing here"; the split says "half of this is yours".
        color: blocked ? '#8A8880' : '#4A3B16',
      };
    else if (fullyHeld && !sel)
      style = { ...style, background: '#FBF1DD', borderColor: '#E3C489', color: '#8A7433' };
    else if (blocked)
      style = { ...style, background: '#F2F1EE', color: '#B4B3AC', textDecoration: 'line-through' };

    if (sel) {
      if (iso === activeIso) {
        style = { ...style, background: '#14532D', borderColor: '#fff', color: '#fff', fontWeight: 700, boxShadow: '0 0 0 2.5px #14532D' };
      } else if (isComplete(sel)) {
        style = { ...style, background: '#14532D', borderColor: '#14532D', color: '#fff', fontWeight: 600 };
      } else {
        style = { ...style, background: '#EAF1EC', borderColor: '#14532D', color: '#1F3D2F', fontWeight: 600 };
      }
    }

    /* Name the actual reason. Most greyed days are simply not open on the
       mediator's schedule, and calling that "booked" sends someone looking for
       a case that isn't there. */
    const blockedWhy = () => {
      const states = SLOTS.map((s) => slotState(iso, s));
      if (states.every((s) => s === 'booked')) return 'Fully booked';
      if (states.every((s) => s === 'held')) return 'Already held';
      if (states.every((s) => s === 'unavailable' || s === 'none'))
        return 'Not open on the mediator’s schedule';
      return 'No half of this day is free to hold';
    };
    // Says WHY the other half is gone, not just that it is.
    const partialWhy = () => {
      const gone = SLOTS.find((sl) => slotState(iso, sl) !== 'holdable');
      const st = gone ? slotState(iso, gone) : 'none';
      const other = free[0] === 'morning' ? 'morning' : 'afternoon';
      const reason =
        st === 'booked'
          ? 'the other half is booked'
          : st === 'held'
          ? 'the other half is on hold'
          : 'the mediator is not open for the other half';
      return `Only ${other} is free — ${reason}`;
    };
    const title = blocked ? blockedWhy() : partial ? partialWhy() : undefined;

    return (
      <button
        key={iso}
        type="button"
        title={title}
        disabled={disabled}
        onClick={() => !disabled && onPick && onPick(iso)}
        style={style}
      >
        {d.getDate()}
        {sel && isComplete(sel) && iso !== activeIso ? (
          <i style={{ position: 'absolute', top: 3, right: 4, fontSize: 9, fontStyle: 'normal', color: 'rgba(255,255,255,.9)' }}>✓</i>
        ) : null}
      </button>
    );
  };

  const sw = (bg, bd) => ({ width: 11, height: 11, borderRadius: 3, background: bg, border: `1.5px solid ${bd}`, display: 'inline-block' });

  return (
    <div style={{ border: '1px solid #E7E5DF', borderRadius: 14, padding: '18px 18px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button type="button" aria-label="Previous month" onClick={prev}
          style={{ background: 'none', border: 0, borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#6E6E67', fontSize: 15 }}>‹</button>
        <h3 style={{ margin: 0, fontFamily: "'Inter Tight', sans-serif", fontSize: 15, fontWeight: 600 }}>
          {new Date(vy, vm, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
        <button type="button" aria-label="Next month" onClick={next}
          style={{ background: 'none', border: 0, borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#6E6E67', fontSize: 15 }}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 6 }}>
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 10.5, fontWeight: 600, color: '#A3A39B', paddingBottom: 4, fontFamily: FONT }}>{d}</div>
        ))}
      </div>

      {/* The grid, with a month still in flight covered by a skeleton of the
          same shape. A skeleton and not a spinner, over the grid rather than
          replacing the card, so the month heading and its arrows stay live.

          It matters more here than in most places because an unloaded calendar
          does not LOOK unloaded — dayMeta null means "assume free",
          deliberately, so a slow request cannot grey out a whole month.
          Without this the operator sees a fully selectable month that silently
          repaints, and a fast click lands on a day that was never free. */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7,1fr)',
            gap: 6,
            marginTop: 2,
            opacity: loading ? 0 : 1,
            transition: 'opacity .18s',
            pointerEvents: loading ? 'none' : undefined,
          }}
          aria-busy={loading || undefined}
        >
          {grid.map((g) => cell(g.date))}
        </div>

        {loading ? (
          <div
            aria-live="polite"
            aria-label="Loading availability"
            style={{
              position: 'absolute',
              inset: 0,
              marginTop: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(7,1fr)',
              gap: 6,
            }}
          >
            {grid.map((g, i) => (
              <div
                key={i}
                style={{
                  aspectRatio: '1 / 1',
                  borderRadius: 9,
                  background: '#F2F1EE',
                  // Staggered so it reads as one sweep across the month rather
                  // than 42 squares blinking in unison.
                  animation: 'holdCalPulse 1.15s ease-in-out infinite',
                  animationDelay: `${(i % 7) * 45}ms`,
                }}
              />
            ))}
            <style>{`@keyframes holdCalPulse{0%,100%{opacity:.45}50%{opacity:.9}}`}</style>
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 10, paddingTop: 9, borderTop: '1px solid #F1F0EC', fontSize: 11, color: '#4B4B45', fontFamily: FONT }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={sw('#14532D', '#14532D')} /> Set</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={sw('#EAF1EC', '#14532D')} /> Waiting</span>
        {/* The swatch is split like the cells are, so the legend teaches the
            shape rather than just naming it. */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <i
            style={{
              ...sw('#FBF1DD', '#E3C489'),
              background: 'linear-gradient(to bottom, #FBF1DD 0 50%, #FFFFFF 50% 100%)',
            }}
          />{' '}
          Half held
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={sw('#F2F1EE', '#DEDDD7')} /> Booked</span>
      </div>
    </div>
  );
}
